import { useRef, type ReactNode, Children } from "react";
import { motion, useInView, type Variants } from "framer-motion";

interface StaggerContainerProps {
  children: ReactNode;
  stagger?: number;
  direction?: "up" | "down" | "left" | "right";
  className?: string;
  style?: React.CSSProperties;
  once?: boolean;
}

export default function StaggerContainer({
  children,
  stagger = 0.1,
  direction = "up",
  className,
  style,
  once = true,
}: StaggerContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once, amount: 0.1, margin: "0px 0px -50px 0px" });

  const getOffset = () => {
    switch (direction) {
      case "up": return { y: 40 };
      case "down": return { y: -40 };
      case "left": return { x: 40 };
      case "right": return { x: -40 };
    }
  };

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger,
      },
    },
  };

  const childVariants: Variants = {
    hidden: {
      opacity: 0,
      ...getOffset(),
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      },
    },
  };

  const items = Children.toArray(children);

  return (
    <motion.div
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className={className}
      style={style}
    >
      {items.map((child, i) => (
        <motion.div key={i} variants={childVariants} style={{ willChange: "transform, opacity" }}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
